import * as React from 'react';

/**
 * 取图失败后重取的等待表：第 n 次失败等 `RETRY_DELAYS_MS[n]`，表用完就停手。
 *
 * 头几档压得短（400ms 起）是因为要救的正是「一批头像同时取、个别请求被挤掉」这类抖动，用户还在
 * 看着这一屏。**不无限重试**：URL 真取不到时，每枚头像每隔十几秒发一次请求会变成一场长跑。
 */
const RETRY_DELAYS_MS = [400, 1200, 3600] as const;

/** 这次失败之后安排的那一次重取。 */
export interface ImageRetryPlan {
  /** 这是第几次重取（从 1 起）。 */
  readonly attempt: number;
  readonly delayMs: number;
}

export interface ImageRetry {
  /**
   * 给 `<img>`（或包着它的组件）当 `key`：换一轮就是一枚新实例，也就是重新发一次请求，而 URL
   * 一个字符不动。**别拿它去改 URL**——加 cache-busting 参数会把每次重试都推成 CDN 回源。
   */
  readonly imageKey: number;
  /**
   * 轮次用完、这个 URL **确定取不回来**了。
   *
   * 给调用方一个换画法的机会：`<img>` 挂着不动会显示浏览器那枚碎图标（或一块空框），而这类图往
   * 往有主色可用（头像的 `portrait_color` 就是那张图自己的底色），画一枚纯色圆比碎图标像话。注意
   * 它**只在轮次用完后**为真——中间那几次失败还在重取，此时换画法会闪。换 `src` 时归零。
   */
  readonly failed: boolean;
  /**
   * 挂到 `onError` 里调用：安排下一次重取并返回这次的安排；轮次用完（或压根没有 URL）返回
   * `null`，调用方可据此记一条「这张图取不回来」。
   */
  readonly retryAfterError: () => ImageRetryPlan | null;
}

/**
 * 取图失败按**瞬时故障**处理，退避重取（见 {@link RETRY_DELAYS_MS}）。
 *
 * 治的是这类现象：断网几秒、或启动那一阵一批头像同时取被挤掉个别请求，于是那一枚永久停在兜底态
 * ——`<img>` 只在挂载和换 `src` 时发请求，而虚拟列表里的行可能很久都不重挂，调用方也没有换 URL
 * 的理由（URL 本来就是对的）。所以重取靠换 `key` 重挂，不靠改 URL。
 *
 * 换 `src` 时轮次归零：新 URL 有自己完整的几次机会，也不会把上一个 URL 在等的那次重取落到它身上。
 */
export function useImageRetry(src: string): ImageRetry {
  const [retry, setRetry] = React.useState({
    src: '',
    attempt: 0,
    failed: false,
  });
  const retryTimer = React.useRef(0);
  if ((retry.attempt > 0 || retry.failed) && retry.src !== src) {
    // 换了 URL：重取轮次与「取不回来」的结论都属于上一个 URL，跟着作废（渲染期就地收，React 认的
    // 用法）。新 URL 有自己完整的几次机会。
    setRetry({ src: '', attempt: 0, failed: false });
  }
  const current = retry.src === src;
  const attempt = current ? retry.attempt : 0;
  React.useEffect(() => {
    // 换 URL / 卸载时撤掉在等的那一次重取：它属于上一个 URL。
    return () => {
      window.clearTimeout(retryTimer.current);
    };
  }, [src]);
  return {
    imageKey: attempt,
    failed: current && retry.failed,
    retryAfterError: () => {
      // 没有 URL 时也可能收到 `error`（如 Base UI 的 `useImageLoadingStatus`）：那不是取图失败，
      // 也就没什么可放弃的。
      if (!src) {
        return null;
      }
      const delayMs = RETRY_DELAYS_MS[attempt];
      if (delayMs === undefined) {
        setRetry({ src, attempt, failed: true });
        return null;
      }
      retryTimer.current = window.setTimeout(() => {
        setRetry({ src, attempt: attempt + 1, failed: false });
      }, delayMs);
      return { attempt: attempt + 1, delayMs };
    },
  };
}
